import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { TourCard } from "./tour-card";
import { TourDialog } from "./tour-dialog";
import type { TourAudienceRecord, TourRecord } from "./types";

interface ToursTabProps {
  audience: TourAudienceRecord;
  openTourRef?: React.MutableRefObject<(() => void) | null>;
}

export function ToursTab({ audience, openTourRef }: ToursTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTour, setEditingTour] = useState<TourRecord | null>(null);

  const toursQuery = useQuery<{ tours: TourRecord[] }>({
    queryKey: ["/api/admin/tours", audience.id],
    queryFn: async () => {
      const res = await fetch(`/api/admin/tours?audienceId=${audience.id}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load tours");
      return res.json();
    },
  });

  const tours = toursQuery.data?.tours ?? [];

  function openNewTour() {
    setEditingTour(null);
    setDialogOpen(true);
  }

  if (openTourRef) {
    openTourRef.current = openNewTour;
  }

  function openEditTour(t: TourRecord) {
    setEditingTour(t);
    setDialogOpen(true);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold" data-testid="text-tours-tab-title">
          {audience.label}
        </h2>
        {audience.description && (
          <p className="text-sm text-muted-foreground">{audience.description}</p>
        )}
      </div>

      {toursQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {tours.map((t) => (
            <TourCard key={t.id} tour={t} audienceId={audience.id} audienceCriteria={audience.criteria} onEdit={openEditTour} />
          ))}
          {tours.length === 0 && (
            <div className="border border-dashed rounded-md p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No tours for this audience yet. Click "Add tour" to create one.
              </p>
            </div>
          )}
        </div>
      )}

      <TourDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        audienceId={audience.id}
        audienceLabel={audience.label}
        tour={editingTour}
      />
    </div>
  );
}
